import React, { useState } from "react";
import { useGame } from "../game/em2c-store";
import { RESOURCES } from "../game/data/resources";
import { BUILDING_LIST } from "../game/data/buildings";
import {
  Zap, Droplet, Wind, Wheat, Layers, Cog, Package, Coins, X, BookOpen,
} from "lucide-react";

const RES_ICONS = { Zap, Droplet, Wind, Wheat, Layers, Cog, Package, Coins };

function ResourceEntry({ meta }) {
  const Icon = RES_ICONS[meta.icon] || Zap;
  return (
    <div className="p-3 border border-white/10 bg-white/[0.02] hover:bg-white/[0.04]" data-testid={`codex-res-${meta.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color: meta.color }} />
        <div className="text-sm font-semibold uppercase tracking-wide text-white">{meta.label}</div>
        <span className="ml-auto text-[10px] font-mono text-slate-500">{meta.unit}</span>
      </div>
      <div className="text-xs font-mono text-slate-300 leading-snug mb-2">{meta.desc}</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-0.5" style={{ color: "#00fa9a" }}>Produced by</div>
          {meta.produced.map((p, i) => <div key={i} className="text-[11px] font-mono text-slate-300 leading-tight">· {p}</div>)}
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-0.5" style={{ color: "#ffc107" }}>Consumed by</div>
          {meta.consumed.map((p, i) => <div key={i} className="text-[11px] font-mono text-slate-300 leading-tight">· {p}</div>)}
        </div>
      </div>
    </div>
  );
}

function BuildingEntry({ b }) {
  const requirementLabel = b.depositLabel || (b.requiresDeposit === "hydrated_minerals" ? "Hydrated Mineral Zone" : b.requiresDeposit);
  return (
    <div className="p-3 border border-white/10 bg-white/[0.02] hover:bg-white/[0.04]" data-testid={`codex-bld-${b.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-5" style={{ background: b.accent, boxShadow: `0 0 8px ${b.accent}` }} />
        <div className="text-sm font-semibold uppercase tracking-wide text-white">{b.name}</div>
        <span className="ml-auto text-[10px] font-mono uppercase text-slate-500">{b.category}</span>
      </div>
      <div className="text-xs font-mono text-slate-300 leading-snug mb-2">{b.description}</div>
      <div className="flex flex-wrap gap-1 mb-2">
        {Object.entries(b.cost || {}).filter(([, v]) => v > 0).map(([k, v]) => {
          const rmeta = RESOURCES[k];
          const Icon = rmeta ? RES_ICONS[rmeta.icon] : Zap;
          return (
            <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 border bg-black/30 text-[10px] font-mono" style={{ borderColor: (rmeta?.color || "#fff") + "60", color: rmeta?.color || "#fff" }}>
              <Icon size={10} />{v} {rmeta?.label || k}
            </span>
          );
        })}
        {(!b.cost || Object.entries(b.cost).filter(([, v]) => v > 0).length === 0) && <span className="text-[11px] font-mono text-slate-500">Free (starter)</span>}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] font-mono">
        {b.power ? <div className="flex justify-between"><span className="text-slate-400">Power</span><span style={{ color: b.power > 0 ? "#00fa9a" : "#ffc107" }}>{b.power > 0 ? "+" : ""}{b.power}</span></div> : null}
        {b.water ? <div className="flex justify-between"><span className="text-slate-400">Clean Water</span><span style={{ color: b.water > 0 ? "#00fa9a" : "#ffc107" }}>{b.water > 0 ? "+" : ""}{b.water}</span></div> : null}
        {b.oxygen ? <div className="flex justify-between"><span className="text-slate-400">Oxygen</span><span style={{ color: b.oxygen > 0 ? "#00fa9a" : "#ffc107" }}>{b.oxygen > 0 ? "+" : ""}{b.oxygen}</span></div> : null}
        {b.food ? <div className="flex justify-between"><span className="text-slate-400">Food</span><span style={{ color: "#00fa9a" }}>+{b.food}</span></div> : null}
        {b.metal ? <div className="flex justify-between"><span className="text-slate-400">Metal</span><span style={{ color: "#00fa9a" }}>+{b.metal}</span></div> : null}
        {b.houses ? <div className="flex justify-between"><span className="text-slate-400">Houses</span><span className="text-white">{b.houses}</span></div> : null}
        {b.buildTime ? <div className="flex justify-between"><span className="text-slate-400">Build</span><span className="text-white">{b.buildTime}s</span></div> : null}
      </div>
      {b.storage && (
        <div className="mt-2 pt-2 border-t border-white/10 text-[11px] font-mono">
          <span className="text-slate-400">Storage: </span>
          {Object.entries(b.storage).map(([k, v]) => <span key={k} className="text-white mr-2">+{v} {RESOURCES[k]?.label || k.replace(/_/g, " ")}</span>)}
        </div>
      )}
      {b.requiresDeposit && (
        <div className="mt-2 pt-2 border-t border-white/10 text-[11px] font-mono" style={{ color: "#ffc107" }}>
          Requires <span className="uppercase">{requirementLabel}</span>
        </div>
      )}
    </div>
  );
}

export default function Codex() {
  const open = useGame((s) => s.codexOpen);
  const close = useGame((s) => s.closeCodex);
  const [tab, setTab] = useState("resources");
  if (!open) return null;

  return (
    <div className="absolute inset-0 z-40 pointer-events-auto flex items-center justify-center bg-black/70 backdrop-blur-sm" style={{ fontFamily: "Rajdhani, sans-serif" }} data-testid="codex-modal">
      <div className="bg-[#0A0C10] border border-white/15 w-[min(1100px,94vw)] h-[min(80vh,780px)] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <BookOpen size={18} className="text-cyan-400" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Mission Handbook</div>
              <div className="text-lg font-semibold uppercase tracking-wide text-white">Colony Codex</div>
            </div>
          </div>
          <button data-testid="btn-close-codex" onClick={close} className="w-8 h-8 flex items-center justify-center border border-white/20 bg-white/5 hover:border-[#ff3366] hover:text-[#ff3366] text-white"><X size={16} /></button>
        </div>

        <div className="flex border-b border-white/10">
          {[{ id: "resources", label: "Resources" }, { id: "buildings", label: "Buildings" }, { id: "howto", label: "How to Play" }].map((t) => (
            <button key={t.id} data-testid={`codex-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-5 py-2.5 text-xs uppercase tracking-widest border-r border-white/10 ${tab === t.id ? "text-cyan-400 bg-cyan-500/5" : "text-slate-400 hover:text-white"}`} style={tab === t.id ? { boxShadow: "inset 0 -2px 0 #00e5ff" } : {}}>{t.label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {tab === "resources" && <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{Object.values(RESOURCES).map((m) => <ResourceEntry key={m.id} meta={m} />)}</div>}
          {tab === "buildings" && <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{BUILDING_LIST.map((b) => <BuildingEntry key={b.id} b={b} />)}</div>}
          {tab === "howto" && (
            <div className="max-w-2xl space-y-4 text-sm font-mono text-slate-300 leading-relaxed">
              <div>
                <div className="text-cyan-400 text-xs uppercase tracking-widest mb-1">Objective</div>
                <p>Keep at least <span className="text-cyan-400">12 colonists</span> alive with positive oxygen, Clean Water, food and power for <span className="text-cyan-400">5 consecutive sols</span>.</p>
              </div>
              <div>
                <div className="text-cyan-400 text-xs uppercase tracking-widest mb-1">Water Chain</div>
                <p><span className="text-white">Survey → Hydrated Mineral Zone → Regolith Water Extractor → Raw Water → Water Purification → Clean Water.</span> Atmospheric Water Harvesters provide a slow, power-hungry backup without a zone.</p>
              </div>
              <div>
                <div className="text-cyan-400 text-xs uppercase tracking-widest mb-1">First Steps</div>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Build a <span className="text-[#ffc107]">Solar Power Station</span> and <span className="text-[#ffc107]">Battery Storage</span>.</li>
                  <li>Survey terrain to identify <span className="text-cyan-400">Hydrated Mineral Zones</span> and metal geology.</li>
                  <li>Place a <span className="text-[#00e5ff]">Regolith Water Extractor</span> directly on the Hydrated Mineral Zone.</li>
                  <li>Build <span className="text-[#00e5ff]">Raw Water Storage</span> → <span className="text-[#00e5ff]">Water Purification</span> → <span className="text-[#00e5ff]">Clean Water Reservoir</span>.</li>
                  <li>Add an <span className="text-[#69d9e8]">Atmospheric Water Harvester</span> when you need emergency deposit-free production.</li>
                  <li>Build oxygen, habitat and food systems before requesting colonists.</li>
                </ol>
              </div>
              <div>
                <div className="text-cyan-400 text-xs uppercase tracking-widest mb-1">Dust Storms</div>
                <p>Dust storms cripple solar output. Batteries and stored life-support resources are your buffer.</p>
              </div>
              <div>
                <div className="text-cyan-400 text-xs uppercase tracking-widest mb-1">Controls</div>
                <ul className="text-xs space-y-0.5">
                  <li><span className="text-cyan-400">Left-drag</span> pan · <span className="text-cyan-400">Right-drag</span> orbit · <span className="text-cyan-400">Wheel</span> zoom</li>
                  <li><span className="text-cyan-400">Space</span> pause · <span className="text-cyan-400">1/2/3</span> speed · <span className="text-cyan-400">R</span> rotate · <span className="text-cyan-400">Esc</span> cancel placement</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
